import { ENVIRONMENT } from '@/environments/environment.ts';
import { csrfStorage } from "@/utils/csrfStorage.ts";
import { getGlobalTenant } from '@/utils/urlUtils';

// Timestamp da última vez que o token foi atualizado
let lastTokenRefresh = 0;
// Tempo de validade do token em milissegundos (15 minutos)
const TOKEN_VALIDITY_MS = 15 * 60 * 1000;

// Para diagnóstico: contador de tentativas
let consecutiveAttempts = 0;
// Para diagnóstico: máximo de tentativas
const MAX_CONSECUTIVE_ATTEMPTS = 5;

// Para diagnóstico: armazenar token em memória
let inMemoryToken: string | null = null;

/**
 * Busca um novo token CSRF do servidor
 */
export async function getCsrfToken(): Promise<void> {
  try {
    // Evitar loop infinito de tentativas
    if (consecutiveAttempts >= MAX_CONSECUTIVE_ATTEMPTS) {
      console.warn('[CSRF] Maximum consecutive attempts reached. Will try again later.');
      // Reiniciar contador após 1 minuto
      setTimeout(() => {
        consecutiveAttempts = 0;
      }, 60000);
      return;
    }

    // Incrementar contador de tentativas
    consecutiveAttempts++;

    // Obter o nome do tenant do contexto global
    const tenantId = getGlobalTenant();

    // Preparar headers, incluindo X-Tenant-Id se disponível
    const headers: HeadersInit = {};
    if (tenantId) {
      headers['X-Tenant-Id'] = tenantId;
    }

    const csrfUrl = `${ENVIRONMENT.baseUrlIcarus}/csrf-token`;

    // Fazer uma requisição GET para obter um novo token, garantindo que aceitamos cookies
    const response = await fetch(csrfUrl, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: headers,
    });

    if (!response.ok) {
      console.error(`Failed to get CSRF token: ${response.status}`);
      throw new Error(`Falha ao obter token CSRF: ${response.status}`);
    }

    // Atualizar o timestamp
    lastTokenRefresh = Date.now();

    // Verificar se o cookie foi definido
    const token = getStoredCsrfToken();

    if (token) {
      // Token obtido com sucesso, resetar contador
      consecutiveAttempts = 0;
    } else {
      console.warn('[CSRF] Token não encontrado nos cookies após requisição bem-sucedida.');
    }
  } catch (error) {
    console.error('[CSRF] Erro ao obter token:', error);
    // Não propagar o erro para não bloquear a UI
  }
}

/**
 * Obtém o token CSRF do cookie ou dos fallbacks
 */
export function getStoredCsrfToken(): string | null {
  // Primeiro tenta obter do cookie (método padrão)
  const cookies = document.cookie.split(';');
  const tokenCookie = cookies.find(cookie => cookie.trim().startsWith('XSRF-TOKEN='));

  if (tokenCookie) {
    const token = decodeURIComponent(tokenCookie.split('=')[1]);
    inMemoryToken = token;

    return token;
  }

  // Se não encontrou no cookie, tenta achar o cookie armazenado em memória
  if (inMemoryToken) {
    return inMemoryToken;
  }

  return null;
}

/**
 * Inicializa o serviço CSRF, configurando um intervalo para atualizar
 * o token periodicamente
 */
export function initCsrfService(): void {
  console.log('Initializing CSRF service');

  // Obter o token inicial
  getCsrfToken().then(() => {
    console.log('Initial CSRF token requested');
  });

  // Configurar um intervalo para atualizar o token a cada 10 minutos
  setInterval(() => {
    console.log('Refreshing CSRF token via interval');
    getCsrfToken();
  }, TOKEN_VALIDITY_MS - (5 * 60 * 1000)); // 5 minutos antes da expiração
}

/**
 * Adiciona o token CSRF aos cabeçalhos de uma requisição, se necessário
 */
export function addCsrfTokenToHeaders(headers: HeadersInit, method: string): HeadersInit {
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase())) {
    const token = getStoredCsrfToken();

    if (token) {
      const newHeaders = {
        ...headers,
        'X-CSRF-Token': token
      };

      return newHeaders;
    } else {
      // Se não temos token, tentar obter um agora
      console.warn('[CSRF] Tentando obter token CSRF antes de continuar');
      getCsrfToken();
    }
  }
  return headers;
}
